import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../utils/format';
import CreateUserModal from '../components/users/CreateUserModal';
import RolesModal from '../components/users/RolesModal';
import PagesModal from '../components/users/PagesModal';
import ProfileModal from '../components/users/ProfileModal';
import ChangeLogsModal from '../components/users/ChangeLogsModal';
import ResetPasswordModal from '../components/users/ResetPasswordModal';

const ROLE_LEVEL = { root: 4, admin: 3, user: 2, readonly: 1 };
const getHighestLevel = (roles) => Math.max(...(roles || []).map((r) => ROLE_LEVEL[r] || 0), 0);
const PAGE_SIZE = 20;

function AdminUsers({ token, backendUrl, userRoles }) {
  const { t } = useTranslation();
  const myLevel = getHighestLevel(userRoles);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  // Modal 狀態：用單一 "active" 來表示
  const [activeModal, setActiveModal] = useState(null); // 'create' | 'roles' | 'pages' | 'profile' | 'logs' | 'pw'
  const [activeUser, setActiveUser] = useState(null);

  const [profileSaving, setProfileSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [changeLogs, setChangeLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const authHeaders = useRef(null);
  authHeaders.current = { headers: { Authorization: `Bearer ${token}` } };

  const fetchUsers = useCallback(async (signal) => {
    setLoading(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const res = await axios.get(`${backendUrl}/users/admin`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip, limit: PAGE_SIZE },
        signal,
      });
      setUsers(res.data.users || []);
      setError(null);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setError(err.response?.data?.detail || t('users.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, page, t]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => controller.abort();
  }, [token, fetchUsers]);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setActiveUser(null);
  }, []);

  const openModal = useCallback((modal, user = null) => {
    setActiveUser(user);
    setActiveModal(modal);
  }, []);

  // --- API 動作 ---
  const handleCreateUser = async (formData) => {
    try {
      await axios.post(`${backendUrl}/users/admin`, formData, authHeaders.current);
      alert(t('users.createSuccess', { username: formData.username }));
      closeModal();
      fetchUsers();
    } catch (err) {
      alert(t('users.createFailed') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleDisable = async (user) => {
    const isCurrentlyDisabled = user.disabled;
    const action = isCurrentlyDisabled ? 'enable' : 'disable';
    const originalUsers = [...users];
    setUsers(users.map((u) => (u.username === user.username ? { ...u, disabled: !isCurrentlyDisabled } : u)));
    try {
      await axios.post(`${backendUrl}/users/admin/${user.username}/${action}`, {}, authHeaders.current);
    } catch (err) {
      alert(t('users.statusUpdateFailed') + ': ' + (err.response?.data?.detail || ''));
      setUsers(originalUsers);
    }
  };

  const handleSaveRoles = async (selectedRoles) => {
    if (!activeUser) return;
    try {
      await axios.put(
        `${backendUrl}/users/admin/${activeUser.username}/roles`,
        selectedRoles,
        authHeaders.current
      );
      alert(t('users.rolesUpdated'));
      closeModal();
      fetchUsers();
    } catch (err) {
      alert(t('users.rolesUpdateFailed') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSavePages = async (selectedPages) => {
    if (!activeUser) return;
    try {
      await axios.put(
        `${backendUrl}/users/admin/${activeUser.username}/visible_pages`,
        selectedPages,
        authHeaders.current
      );
      alert(t('users.pagesUpdated'));
      closeModal();
      fetchUsers();
    } catch (err) {
      alert(t('users.pagesUpdateFailed') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveProfile = async (profileData) => {
    if (!activeUser) return;
    setProfileSaving(true);
    try {
      await axios.put(
        `${backendUrl}/users/admin/${activeUser.username}/profile`,
        profileData,
        authHeaders.current
      );
      alert(t('users.profileUpdated'));
      closeModal();
      fetchUsers();
    } catch (err) {
      alert(t('users.profileUpdateFailed') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!activeUser || !newPassword) return;
    setPwSaving(true);
    try {
      await axios.put(
        `${backendUrl}/users/admin/${activeUser.username}/password`,
        { new_password: newPassword },
        authHeaders.current
      );
      alert(t('users.pwResetSuccess', { username: activeUser.username }));
      closeModal();
    } catch (err) {
      alert(t('users.pwResetFailed') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setPwSaving(false);
    }
  };

  const openLogs = async (user) => {
    openModal('logs', user);
    setLogLoading(true);
    setChangeLogs([]);
    try {
      const res = await axios.get(`${backendUrl}/users/admin/${user.username}/change_logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 },
      });
      setChangeLogs(res.data.logs || []);
    } catch {
      setChangeLogs([]);
    } finally {
      setLogLoading(false);
    }
  };

  const canManage = (user) => myLevel > getHighestLevel(user.roles);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{t('users.title')}</h2>
        <button className="btn btn-success" onClick={() => openModal('create')}>
          <i className="bi bi-person-plus-fill"></i> {t('users.addUser')}
        </button>
      </div>

      {error && <div className="alert alert-danger" role="alert">{t('common.error')}: {error}</div>}

      <div className="table-responsive card shadow-sm">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-secondary">
            <tr>
              <th>{t('users.accountName')}</th>
              <th>{t('users.contactInfo')}</th>
              <th>{t('users.rolePermissions')}</th>
              <th>{t('users.statusCol')}</th>
              <th>{t('users.lastLogin')}</th>
              <th>{t('common.operations')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-4">{t('common.loading')}</td></tr>
            ) : users.map((user) => (
              <tr key={user.username}>
                <td>
                  <div className="fw-bold">{user.username}</div>
                  <small className="text-muted">{user.full_name || '-'}</small>
                </td>
                <td>
                  <div style={{ fontSize: '0.9em' }}>
                    {user.email && <div><i className="bi bi-envelope"></i> {user.email}</div>}
                    {user.phone_number && <div><i className="bi bi-phone"></i> {user.phone_number}</div>}
                    {user.telegram_chat_id && <div><i className="bi bi-telegram"></i> {user.telegram_chat_id}</div>}
                    {user.telegram_name && <div><i className="bi bi-person"></i> {user.telegram_name}</div>}
                  </div>
                </td>
                <td>
                  {user.roles.map((role) => (
                    <span key={role} className={`badge me-1 ${role === 'root' ? 'bg-dark' : role === 'admin' ? 'bg-danger' : role === 'user' ? 'bg-primary' : 'bg-secondary'}`}>
                      {role}
                    </span>
                  ))}
                </td>
                <td>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`switch-${user.username}`}
                      checked={!user.disabled}
                      onChange={() => handleToggleDisable(user)}
                      disabled={!canManage(user)}
                    />
                    <label className="form-check-label" htmlFor={`switch-${user.username}`}>
                      {!user.disabled ? <span className="text-success">{t('users.enabled')}</span> : <span className="text-muted">{t('users.disabled')}</span>}
                    </label>
                  </div>
                </td>
                <td style={{ fontSize: '0.85em' }}>
                  <div>{user.last_login_time ? formatDateTime(user.last_login_time) : t('users.neverLoggedIn')}</div>
                  <div className="text-muted">{user.last_login_ip}</div>
                </td>
                <td>
                  {canManage(user) ? (
                    <>
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal('profile', user)} title={t('users.editProfile')} aria-label={t('users.editProfile')}>
                        <i className="bi bi-pencil" aria-hidden="true"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openModal('roles', user)} title={t('users.editRoles')} aria-label={t('users.editRoles')}>
                        <i className="bi bi-shield" aria-hidden="true"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-info me-1" onClick={() => openModal('pages', user)} title={t('users.editPages')} aria-label={t('users.editPages')}>
                        <i className="bi bi-layout-sidebar" aria-hidden="true"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-warning me-1" onClick={() => openModal('pw', user)} title={t('users.resetPassword')} aria-label={t('users.resetPassword')}>
                        <i className="bi bi-key" aria-hidden="true"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-dark" onClick={() => openLogs(user)} title={t('users.viewChangeLogs')} aria-label={t('users.viewChangeLogs')}>
                        <i className="bi bi-clock-history" aria-hidden="true"></i>
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-outline-dark" onClick={() => openLogs(user)} title={t('users.viewChangeLogs')} aria-label={t('users.viewChangeLogs')}>
                      <i className="bi bi-clock-history" aria-hidden="true"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end mt-3">
        <button className="btn btn-outline-secondary me-2" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t('common.prevPage')}</button>
        <button className="btn btn-outline-secondary" disabled={users.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>{t('common.nextPage')}</button>
      </div>

      {activeModal === 'create' && (
        <CreateUserModal onClose={closeModal} onSubmit={handleCreateUser} />
      )}
      {activeModal === 'roles' && activeUser && (
        <RolesModal user={activeUser} onClose={closeModal} onSave={handleSaveRoles} />
      )}
      {activeModal === 'pages' && activeUser && (
        <PagesModal user={activeUser} onClose={closeModal} onSave={handleSavePages} />
      )}
      {activeModal === 'profile' && activeUser && (
        <ProfileModal user={activeUser} saving={profileSaving} onClose={closeModal} onSave={handleSaveProfile} />
      )}
      {activeModal === 'logs' && activeUser && (
        <ChangeLogsModal user={activeUser} logs={changeLogs} loading={logLoading} onClose={closeModal} />
      )}
      {activeModal === 'pw' && activeUser && (
        <ResetPasswordModal user={activeUser} saving={pwSaving} onClose={closeModal} onSave={handleResetPassword} />
      )}
    </div>
  );
}

export default AdminUsers;
